<script setup>
/**
 * 上映スケジュールAI：対象期間の選択（タイムチャート作成・出力・共有で共用）
 *
 * - 開始日〜終了日の範囲選択（両端を含めて最大 14 日、開始曜日は自由）
 * - ‹ › で同じ日数だけ前後の期間に移動
 * - 保存済みの期間一覧（GET /plan/periods）からすぐに開ける。準拠判定も表示
 * - カレンダー上で保存済みの期間に含まれる日（排班済みの日）を強調表示する。選択は制限しない
 * 期間の切り替えは change イベントで親に任せる（未保存の確認・URL の更新は親が行う）。
 */
import { ref, computed } from "vue";
import { ElMessage } from "element-plus";
import {
  MAX_PERIOD_DAYS,
  addDays,
  formatDate,
  isValidPeriod,
  periodDates,
  periodDayCount,
  periodRangeLabel,
} from "../utils";

const props = defineProps({
  dateFrom: { type: String, default: "" },
  dateTo: { type: String, default: "" },
  // 保存済みの期間 [{ dateFrom, dateTo, compliance, generatedTime, updateTime }]（開始日の降順）
  periods: { type: Array, default: () => [] },
});
const emit = defineEmits(["change"]);

// 1 日のミリ秒
const DAY_MS = 86400000;
// 準拠判定の表示（未生成・上映回なしは null）
const COMPLIANCE_TAGS = {
  COMPLIANT: { label: "実行可能", type: "success" },
  NON_COMPLIANT: { label: "非準拠", type: "danger" },
};
const complianceTag = (value) => COMPLIANCE_TAGS[value] || { label: "未作成", type: "info" };

const pickerValue = computed(() => (props.dateFrom && props.dateTo ? [props.dateFrom, props.dateTo] : null));
const dayCount = computed(() => periodDayCount(props.dateFrom, props.dateTo));

// ==================== 排班済みの日の強調 ====================

// 保存済みの期間に含まれる日（yyyy-MM-dd）の集合
const scheduledDates = computed(() => {
  const set = new Set();
  props.periods.forEach((p) => periodDates(p.dateFrom, p.dateTo).forEach((d) => set.add(d)));
  return set;
});

/**
 * カレンダーのセルに付けるクラス（排班済みの日は is-scheduled）
 */
const cellClassName = (date) => (scheduledDates.value.has(formatDate(date)) ? "is-scheduled" : "");

// ==================== 範囲選択 ====================

// 範囲選択の途中で最初にクリックした日（2 日目の候補を 14 日以内に絞るため）
const pickingStart = ref(null);

/**
 * カレンダー上のクリック。1 日目だけ選んだ状態なら [Date, null] が来る
 */
const onCalendarChange = (value) => {
  pickingStart.value = value?.[0] && !value?.[1] ? value[0] : null;
};

/**
 * 1 日目を選んだ後は、その日から前後 14 日（両端を含む）を超える日を選べないようにする
 */
const disabledDate = (date) => {
  if (!pickingStart.value) return false;
  return Math.round(Math.abs(date - pickingStart.value) / DAY_MS) >= MAX_PERIOD_DAYS;
};

const onVisibleChange = (visible) => {
  if (!visible) pickingStart.value = null;
};

const onPick = (value) => {
  pickingStart.value = null;
  if (!value) return;
  const [from, to] = value;
  if (!isValidPeriod(from, to)) {
    ElMessage.warning(`期間は${MAX_PERIOD_DAYS}日以内で選択してください`);
    return;
  }
  if (from === props.dateFrom && to === props.dateTo) return;
  emit("change", { dateFrom: from, dateTo: to });
};

/**
 * 同じ日数だけ前後にずらす（direction: -1 前 / 1 次）
 */
const shift = (direction) => {
  const count = dayCount.value;
  if (!count) return;
  emit("change", {
    dateFrom: addDays(props.dateFrom, direction * count),
    dateTo: addDays(props.dateTo, direction * count),
  });
};

// ==================== 保存済みの期間 ====================

const isCurrent = (p) => p.dateFrom === props.dateFrom && p.dateTo === props.dateTo;

const openSaved = (p) => {
  if (isCurrent(p)) return;
  emit("change", { dateFrom: p.dateFrom, dateTo: p.dateTo });
};
</script>

<template>
  <div class="period-picker">
    <el-button :aria-label="`前の${dayCount}日間`" :disabled="!dayCount" @click="shift(-1)">
      <el-icon><ArrowLeft /></el-icon>
    </el-button>
    <el-date-picker
      :model-value="pickerValue"
      type="daterange"
      value-format="YYYY-MM-DD"
      format="YYYY/MM/DD"
      range-separator="〜"
      start-placeholder="開始日"
      end-placeholder="終了日"
      :clearable="false"
      :editable="false"
      :disabled-date="disabledDate"
      :cell-class-name="cellClassName"
      popper-class="period-picker-popper"
      class="period-range"
      @calendar-change="onCalendarChange"
      @visible-change="onVisibleChange"
      @update:model-value="onPick"
    />
    <el-button :aria-label="`次の${dayCount}日間`" :disabled="!dayCount" @click="shift(1)">
      <el-icon><ArrowRight /></el-icon>
    </el-button>
    <span v-if="dayCount" class="period-days" :title="periodRangeLabel(dateFrom, dateTo)">{{ dayCount }}日間</span>

    <el-dropdown v-if="periods.length" trigger="click" max-height="320px" @command="openSaved">
      <el-button>
        保存済みの期間<span class="saved-count">{{ periods.length }}</span>
        <el-icon class="el-icon--right"><ArrowDown /></el-icon>
      </el-button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
            v-for="p in periods"
            :key="`${p.dateFrom}~${p.dateTo}`"
            :command="p"
            :class="{ 'is-current': isCurrent(p) }"
          >
            <span class="saved-item">
              <span class="saved-item__label">{{ periodRangeLabel(p.dateFrom, p.dateTo) }}</span>
              <el-tag :type="complianceTag(p.compliance).type" size="small" effect="plain">
                {{ complianceTag(p.compliance).label }}
              </el-tag>
              <span v-if="isCurrent(p)" class="saved-item__current">表示中</span>
            </span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
</template>

<style lang="less">
/* カレンダーはポップアップで body 直下に出るためスコープなしで指定する */
.period-picker-popper {
  // 排班済みの日：日付の下に印を付け、文字色も変える（範囲選択中の背景色とも見分けられるようにする）
  .el-date-table td.is-scheduled .el-date-table-cell {
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: 1px;
      left: 50%;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--el-color-warning);
      transform: translateX(-50%);
    }
  }

  .el-date-table td.is-scheduled:not(.start-date):not(.end-date) .el-date-table-cell__text {
    font-weight: 700;
    color: var(--el-color-warning-dark-2);
  }
}
</style>

<style lang="less" scoped>
/* 親ページの --sp-* トークンを継承して使う */
.period-picker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;

  .el-button + .el-button,
  .el-button + .el-date-editor,
  .el-date-editor + .el-button {
    margin-left: 0;
  }

  // 日付ピッカーのルートにはスコープ属性が付かないため :deep で指定する（既定の 350px は広すぎるので詰める）
  :deep(.period-range.el-date-editor) {
    width: 250px;
    box-sizing: border-box;
  }
}

.period-days {
  padding: 2px 8px;
  border: 1px solid var(--sp-border, #e5e5e5);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--sp-text-2, #4a4a4a);
  white-space: nowrap;
}

.saved-count {
  margin-left: 6px;
  font-variant-numeric: tabular-nums;
  color: var(--sp-muted, #757575);
}

.saved-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &__label {
    font-variant-numeric: tabular-nums;
  }

  &__current {
    font-size: 11px;
    color: var(--el-text-color-secondary);
  }
}

.is-current {
  font-weight: 700;
}
</style>
